import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { CombineIcon } from 'lucide-react'

import {
  ActionsMenu,
  BreadcrumbTrail,
  DropdownMenuItem,
  Loader,
  PageHeader,
  Skeleton,
} from '@/components/ui'
import { useDocumentTitle, useToast } from '@/hooks'
import { isSingleProjectPlan, useAccount } from '@/modules/account'
import {
  useFolder,
  useFoldersMutating,
  useFoldersMutations,
  useLoadingFolder,
} from '@/modules/folders'
import { useLoadingProjects, useProjects } from '@/modules/projects'
import { ProjectsList } from '@/modules/projects/components'
import { isProjectBusy } from '@/modules/projects/status'
import { getProjectPath } from '@/utils'

export const Route = createFileRoute('/_authenticated/projects/folder/$slug/')({
  staticData: { dynamicTitle: true },
  component: FolderView,
  shouldReload: false,
})

export function FolderView() {
  const navigate = useNavigate()
  const { slug } = Route.useParams()
  const folder = useFolder()
  const isLoading = useLoadingFolder()
  const { combineFolder } = useFoldersMutations()
  const isMutating = useFoldersMutating()
  const projects = useProjects()
  const isLoadingProjects = useLoadingProjects()
  const account = useAccount()
  const { errorToast } = useToast()
  useDocumentTitle(folder?.name ?? 'Folder')

  const name = isLoading ? <Skeleton className="w-32" /> : folder?.name

  const enableActionsMenu =
    folder &&
    account &&
    !isSingleProjectPlan(account.planKind) &&
    !isLoadingProjects &&
    projects?.length > 1
  const hasBusyProject = projects.some(isProjectBusy)
  const canCombine = !isMutating && !isLoadingProjects && !hasBusyProject

  const combine = (slug: string) => {
    combineFolder(slug)
      .then(({ slug }) => navigate({ to: getProjectPath({ slug }) }))
      .catch((error) =>
        errorToast("We couldn't start combining this folder", error as Error),
      )
  }

  return (
    <div className="space-y-6">
      <BreadcrumbTrail
        crumbs={[{ label: 'Projects', to: '/projects' }]}
        page={name}
      />

      <PageHeader title={name} subtitle="Projects in this folder.">
        {enableActionsMenu && (
          <ActionsMenu>
            <DropdownMenuItem
              onClick={() => combine(folder.slug)}
              disabled={!canCombine}
            >
              {isMutating ? (
                <Loader className="size-4" />
              ) : (
                <CombineIcon className="h-4 w-4" />
              )}
              {isMutating ? 'Combining…' : 'Combine into a project'}
            </DropdownMenuItem>
            {hasBusyProject && !isMutating && (
              <p className="px-2 pb-1.5 text-muted-foreground text-xs">
                Wait for every project to finish processing.
              </p>
            )}
          </ActionsMenu>
        )}
      </PageHeader>

      <ProjectsList folderSlug={slug} />
    </div>
  )
}
